import { Fragment, useState, useMemo } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import { Box, Typography, Link, Tooltip, CircularProgress, Backdrop } from '@mui/material';

// Custom Components
import { Spinner } from '..';
import TooltipTitle from './Calendar/TooltipTitle';

// Utils
import messages from '../../static/messages';

// Services
import appointmentsServices from '../../services/appointmentsServices';

// Styles
import { links_styles } from './Calendar/styles';

const CancelledCalendar = ({ 
  date, 
  appointments, 
  queue,
  baseColumns,
  refreshAppointments,
}) => {
  const [currentItem, setCurrentItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const confirm = useConfirm();
  moment.tz.setDefault('America/Los_Angeles');

  // Calculate display columns with appointments - CLEAN SOLUTION using only lastAssignedUser
  const displayColumns = useMemo(() => {
    if (!baseColumns || !appointments) return null;
    
    // Deep copy base columns
    const columnsCopy = baseColumns.map(col => ({
      ...col,
      info: { ...col.info },
      items: []
    }));

    // Distribute appointments to columns by lastAssignedUser ONLY
    appointments.forEach((apt) => {
      const techId = apt.lastAssignedUser?.id || null;
      const column = columnsCopy.find(col => col.info.tech_id === techId);
      
      if (column) {
        column.items.push(apt);
      } else {
        // If no matching column, add to Unassigned
        const unassignedCol = columnsCopy.find(col => col.info.isUnassigned);
        if (unassignedCol) {
          unassignedCol.items.push(apt);
        }
      }
    });

    // Sort items in each column by createdAt (newest first)
    columnsCopy.forEach(col => {
      col.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });

    return columnsCopy;
  }, [baseColumns, appointments]);

  // Drag handlers
  const dragStartHandler = (e, col, item) => {
    setCurrentItem(item);
    e.target.style.opacity = '0.5';
  };

  const dragEndHandler = (e) => {
    e.target.style.opacity = '1';
    setCurrentItem(null);
  };

  const dragOverHandler = (e) => {
    e.preventDefault();
    e.target.style.boxShadow = 'inset 0 0 30px 0 #ccc';
  };

  const dragLeaveHandler = (e) => {
    e.target.style.boxShadow = 'none';
  };

  // Drop to Queue - restore appointment
  const dropToQueueHandler = async (e) => {
    e.preventDefault();
    e.target.style.boxShadow = 'none';
    
    if (!currentItem || isLoading) return;

    confirm({
      title: 'Restore Appointment',
      description: `Are you sure you want to restore this appointment to the queue? It will be set to Active status.`,
    })
      .then(async () => {
        setIsLoading(true);
        try {
          const response = await appointmentsServices.restoreToQueue(currentItem.id);
          
          if (response.status === 200) {
            toast.success('Appointment restored to queue');
            refreshAppointments();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.error(error);
        } finally {
          setIsLoading(false);
          setCurrentItem(null);
        }
      })
      .catch(() => {
        setCurrentItem(null);
      });
  };

  // Show spinner while computing columns
  if (!displayColumns) return <Spinner />;

  // Calculate column width based on number of technicians (fit in 1080p)
  const numColumns = displayColumns.length;
  // Removed fixed columnWidth - using flex: 1 for equal distribution

  return (
    <Fragment>
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Restoring appointment...
          </Typography>
        </Box>
      </Backdrop>

      {/* Queue Row - compact for 1080p */}
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            minHeight: 50,
            border: '2px dashed #4CAF50',
            backgroundColor: '#E8F5E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
          onDragOver={dragOverHandler}
          onDragLeave={dragLeaveHandler}
          onDrop={dropToQueueHandler}
        >
          <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
            ↓ QUEUE - Drop here to restore ({queue?.length || 0} in queue) ↓
          </Typography>
        </Box>
      </Box>

      {/* Main Calendar Grid - compact columns for 1080p */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        overflowX: 'auto',
        pb: 1,
      }}>
        {displayColumns?.filter(col => col.info.name !== 'Queue').map((col, col_index) => {
          const hasItems = col.items.length > 0;
          
          return (
            <Box 
              key={col_index} 
              sx={{ 
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Column Header - compact */}
              <Box 
                sx={{ 
                  backgroundColor: col.info.color || '#808080',
                  opacity: col.info.isUnassigned ? 0.7 : 1,
                  p: 0.75,
                  borderRadius: '4px 4px 0 0',
                  textAlign: 'center',
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: 12, 
                    fontWeight: 700, 
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {col.info.name}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 10, color: 'rgba(255,255,255,0.8)' }}>
                  ({col.items.length})
                </Typography>
              </Box>

              {/* Appointments List - compact */}
              <Box sx={{ 
                minHeight: 150, 
                maxHeight: 'calc(100vh - 280px)', 
                overflowY: 'auto',
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
              }}>
                {!hasItems ? (
                  <Typography 
                    variant="caption" 
                    sx={{ p: 1, color: '#999', fontStyle: 'italic', textAlign: 'center', display: 'block' }}
                  >
                    No cancelled
                  </Typography>
                ) : (
                  col.items.map((item, item_index) => (
                    <Box
                      key={item.id || item_index}
                      sx={{
                        backgroundColor: col.info.color || '#808080',
                        opacity: 0.85,
                        m: 0.5,
                        p: 0.75,
                        borderRadius: 0.5,
                        cursor: 'grab',
                        transition: 'all 0.15s',
                        '&:hover': {
                          opacity: 1,
                          transform: 'scale(1.02)',
                        },
                      }}
                      draggable
                      onDragStart={(e) => dragStartHandler(e, col, item)}
                      onDragEnd={dragEndHandler}
                    >
                      <Tooltip 
                        arrow 
                        placement="right" 
                        title={<TooltipTitle info={item} />}
                        disableInteractive
                      >
                        <Link 
                          href={`/appointments/${item?.id}`} 
                          underline="none" 
                          sx={{ ...links_styles, display: 'block' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 13, color: '#fff', lineHeight: 1.2 }}>
                            {item?.property?.zipCode || item?.property?.address?.match(/\d{5}/)?.[0] || 'No ZIP'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 10, color: 'rgba(255,255,255,0.9)', display: 'block' }}>
                            {item?.type?.name || 'No type'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 9, color: '#ffecb3', display: 'block' }}>
                            {item.startTime ? moment(item.startTime).format('MM/DD') : '-'}
                          </Typography>
                          {item.reasons?.[0]?.note && (
                            <Typography variant="caption" sx={{ 
                              fontSize: 8, 
                              color: '#ffcdd2', 
                              display: 'block',
                              fontStyle: 'italic',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              ✗ {item.reasons[0].note.substring(0, 20)}...
                            </Typography>
                          )}
                        </Link>
                      </Tooltip>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Fragment>
  );
};

export default CancelledCalendar;
